import { Divider } from "@portal/BaseComponents/Divider/AntDivider";
import { CustomFiledFilterGeneric } from "@portal/BaseComponents/Filters/Elements/CustomField/CustomFiledFilter";
import { FilterDropDownButton } from "@portal/BaseComponents/Filters/FilterDropdown/FilterDropDownButton";
import { FilterStrip } from "@portal/BaseComponents/Filters/Strip/FilterStrip";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { MenuClick } from "@portal/BaseComponents/Menu/type";
import { useCustomFieldsQuery } from "@portal/queries/custom-field-query";
import { invoiceFiltersAtom } from "@portal/store/Filters/Invoices/invoice-filter";
import { invoiceDefaultKeys } from "@portal/store/Filters/Invoices/predicate";
import { InvoiceDefaultKeys } from "@portal/store/Filters/Invoices/type";
import { customFieldAtom } from "@portal/store/Filters/customFields/custom-field";
import { InvoiceStatusesConfig } from "@portal/types/entities/invoice";
import produce from "immer";
import { useAtom, useSetAtom } from "jotai";
import { Fragment, useMemo } from "react";
import { DerivedStatusFilter } from "./DerivedStatus/DerivedStatusFilter";
import { DueDateFilter } from "./DueDate/DueDateFilter";
import { InvoiceStatusFilter } from "./InvoiceStatus/InvoiceStatusFilter";
import { IssueDateFilter } from "./IssueDate/IssueDateFilter";

export type InvoiceActionFeatures = {
  draftInvoice: boolean;
  draftMetric: boolean;
};
export interface InvoiceFilterProps {
  actionFeatures?: InvoiceActionFeatures;
  invoiceStatusConfig: InvoiceStatusesConfig;
}

function InvoiceFilters(props: InvoiceFilterProps) {
  const [invoiceFilters, setInvoiceFilters] = useAtom(invoiceFiltersAtom);
  const setFilters = useSetAtom(invoiceFiltersAtom);
  const { data } = useCustomFieldsQuery("INVOICE", setFilters);

  const [customFieldFilter, setCustomField] = useAtom(customFieldAtom);

  function getSelectedFilterKeys() {
    return [
      ...invoiceFilters.activeFilterKeys,
      ...(customFieldFilter.selectedKeys.INVOICES ?? []),
    ];
  }

  function handleClick(e: MenuClick["0"]) {
    if (invoiceDefaultKeys.includes(e.key as InvoiceDefaultKeys)) {
      setInvoiceFilters((filter) => {
        return produce(filter, (draft) => {
          draft.activeFilterKeys.push(e.key as InvoiceDefaultKeys);
        });
      });
      return;
    }
    setCustomField((field) => {
      return produce(field, (draft) => {
        draft.selectedKeys.INVOICES?.push(e.key);
        return draft;
      });
    });
  }

  const customFieldFilters = data ? (
    <Fragment>
      {data.map((field) => {
        return (
          <CustomFiledFilterGeneric
            customField={field}
            key={`${field.display_name}-${field.id}`}
            customFieldKey="INVOICES"
          />
        );
      })}
    </Fragment>
  ) : null;

  const selectedValues = useMemo(getSelectedFilterKeys, [
    customFieldFilter.selectedKeys,
    invoiceFilters.activeFilterKeys,
  ]);

  const stripWidth =
    props.actionFeatures?.draftMetric || props.actionFeatures?.draftInvoice
      ? "254px"
      : "192px";
  return (
    <FilterStrip width={stripWidth}>
      <DerivedStatusFilter
        actionFeatures={props.actionFeatures}
        invoiceStatusConfig={props.invoiceStatusConfig}
      />
      <Flex align="start" space="12">
        <Divider type="vertical" style={{ margin: 0, height: "32px" }} />
        <Flex style={{ width: "100%" }} wrap>
          <InvoiceStatusFilter
            invoiceStatusConfig={props.invoiceStatusConfig}
          />
          <DueDateFilter />
          <IssueDateFilter />
          {customFieldFilters}
          <FilterDropDownButton
            handleClick={handleClick}
            items={invoiceFilters.filterGroup}
            selectedValues={selectedValues}
            key={selectedValues.length}
          />
        </Flex>
      </Flex>
    </FilterStrip>
  );
}

export { InvoiceFilters };
